import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, CaretDown, X } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Searchable item picker. Fetches matches from server (debounced, top-100).
 * Falls back to client-side filter of `items` prop when server unreachable.
 * value: item id, onChange: (item_id, item) => void
 * items: (fallback / already-loaded cache) [{id, item_code, item_name, brand, current_stock, ...}]
 */
export default function ItemSearchSelect({ value, onChange, items, testid, placeholder = "Type code, name or brand..." }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCache, setSelectedCache] = useState(null);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  const selected = (items || []).find(i => i.id === value) || selectedCache;

  // If we have a value but no item info yet, fetch it once
  useEffect(() => {
    if (value && !selected) {
      api.get(`/items/${value}`).then(r => setSelectedCache(r.data)).catch(() => {});
    }
  }, [value, selected]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced server search
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/items?q=${encodeURIComponent(q)}&limit=100`);
        setResults(data);
      } catch {
        // Fallback to client-side filter of preloaded items
        const s = q.toLowerCase().trim();
        setResults(!s ? (items || []).slice(0, 100) : (items || []).filter(i =>
          (i.item_code || "").toLowerCase().includes(s) ||
          (i.item_name || "").toLowerCase().includes(s) ||
          (i.brand || "").toLowerCase().includes(s)
        ).slice(0, 100));
      }
      setLoading(false);
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q, open, items]);

  const pick = (it) => {
    setSelectedCache(it);
    onChange(it.id, it);
    setQ("");
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    setSelectedCache(null);
    onChange("", null);
    setQ("");
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      {selected && !open ? (
        <div
          onClick={() => { setOpen(true); setQ(""); }}
          className="w-full h-10 px-3 pr-16 border border-border rounded-md bg-card text-sm flex items-center cursor-text"
        >
          <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded mr-2">{selected.item_code}</span>
          {(selected.item_type || "GOODS") === "SERVICE" && (
            <span className="text-[10px] font-semibold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded mr-2">SVC</span>
          )}
          <span className="truncate flex-1">{selected.item_name}</span>
          {selected.brand && <span className="text-xs text-muted-foreground ml-2">({selected.brand})</span>}
          <button onClick={clear} className="absolute right-8 top-2.5 text-muted-foreground hover:text-destructive" data-testid={`${testid}-clear`}>
            <X size={14} />
          </button>
          <CaretDown size={14} className="absolute right-3 top-3 text-muted-foreground pointer-events-none" />
        </div>
      ) : (
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-3 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); setHighlight(0); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (!open) return;
              if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
              else if (e.key === "Enter" && results[highlight]) { e.preventDefault(); pick(results[highlight]); }
              else if (e.key === "Escape") setOpen(false);
            }}
            placeholder={placeholder}
            className="pl-9 h-10 text-sm"
            data-testid={`${testid}-input`}
          />
        </div>
      )}

      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-card border border-border rounded shadow-lg max-h-72 overflow-auto">
          {loading ? (
            <div className="p-3 text-xs text-muted-foreground text-center">Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground text-center">{q ? `No items match "${q}"` : "Type to search…"}</div>
          ) : results.map((it, i) => (
            <div
              key={it.id}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              onMouseEnter={() => setHighlight(i)}
              className={`px-3 py-2 text-sm cursor-pointer border-b border-border/50 last:border-0 ${
                i === highlight ? "bg-secondary" : ""
              }`}
              data-testid={`${testid}-opt-${it.item_code}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs bg-secondary px-1.5 py-0.5 rounded shrink-0">{it.item_code}</span>
                {(it.item_type || "GOODS") === "SERVICE" && (
                  <span className="text-[10px] font-semibold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded shrink-0">SVC</span>
                )}
                <span className="flex-1 truncate">{it.item_name}</span>
                {it.brand && <span className="text-xs text-muted-foreground">{it.brand}</span>}
              </div>
              {(it.item_type || "GOODS") === "SERVICE" ? (
                <div className="text-[11px] text-muted-foreground mt-0.5">Service · Sale: ₹{it.sale_price ?? 0}</div>
              ) : (typeof it.current_stock !== "undefined" && (
                <div className="text-[11px] text-muted-foreground mt-0.5">Stock: {it.current_stock ?? 0} · Sale: ₹{it.sale_price ?? 0}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
